import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import toast from 'react-hot-toast';
import { TrendingUp, LogOut, Users, DollarSign, Activity, CheckCircle } from 'lucide-react';

import AdminNavbar from '../components/AdminNavbar';

const AdminDashboard = () => {
  const { logout } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingKYC, setPendingKYC] = useState([]);
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashboardRes, usersRes, kycRes, depositsRes] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/users?limit=10'),
        api.get('/admin/kyc/pending'),
        api.get('/fiat/admin/pending-deposits')
      ]);
      setStats(dashboardRes.data.dashboard);
      setUsers(usersRes.data.users);
      setPendingKYC(kycRes.data.kycSubmissions);
      setPendingDeposits(depositsRes.data.deposits);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleKYCApproval = async (kycId, status) => {
    try {
      await api.put(`/admin/kyc/${kycId}/review`, { status });
      toast.success(`KYC ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update KYC');
    }
  };

  const handleApproveDeposit = async (transactionId) => {
    try {
      await api.post('/fiat/admin/approve-deposit', { transactionId });
      toast.success('Deposit approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve deposit');
    }
  };

  const handleRejectDeposit = async (transactionId) => {
    const comment = prompt('Enter rejection reason:');
    if (!comment) return;

    try {
      await api.post('/fiat/admin/reject-deposit', { transactionId, comment });
      toast.success('Deposit rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject deposit');
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <AdminNavbar />

      <div className="container mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold text-white mb-8">Admin Dashboard</h1>

        {loading ? (
          <div className="text-center py-20 text-gray-400">Loading...</div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-slate-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-400">Total Users</span>
                  <Users className="text-blue-400" size={24} />
                </div>
                <div className="text-3xl font-bold text-white">{stats?.users?.total || 0}</div>
                <div className="text-sm text-gray-400 mt-2">
                  {stats?.users?.active || 0} active
                </div>
              </div>

              <div className="bg-slate-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-400">24h Volume</span>
                  <DollarSign className="text-green-400" size={24} />
                </div>
                <div className="text-3xl font-bold text-white">
                  ${(stats?.trading?.volume24h || 0).toLocaleString()}
                </div>
                <div className="text-sm text-gray-400 mt-2">
                  {stats?.trading?.trades24h || 0} trades
                </div>
              </div>

              <div className="bg-slate-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-400">Pending KYC</span>
                  <Activity className="text-yellow-400" size={24} />
                </div>
                <div className="text-3xl font-bold text-white">{stats?.kyc?.pending || 0}</div>
                <div className="text-sm text-gray-400 mt-2">Awaiting review</div>
              </div>

              <div className="bg-slate-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-400">Pending Withdrawals</span>
                  <CheckCircle className="text-purple-400" size={24} />
                </div>
                <div className="text-3xl font-bold text-white">{stats?.withdrawals?.pending || 0}</div>
                <div className="text-sm text-gray-400 mt-2">Need approval</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Recent Users */}
              <div className="bg-slate-800 rounded-xl p-6">
                <h2 className="text-xl font-bold text-white mb-4">Recent Users</h2>
                <div className="space-y-3">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                      <div>
                        <div className="font-semibold text-white">{user.email}</div>
                        <div className="text-sm text-gray-400">
                          {user.firstName} {user.lastName}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-semibold ${user.status === 'active' ? 'text-green-400' :
                          user.status === 'suspended' ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                          {user.status}
                        </div>
                        <div className="text-xs text-gray-400 mb-2">{user.kycStatus}</div>
                        {user.status === 'active' && (
                          <button
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to block this user? They will be logged out immediately.')) {
                                try {
                                  await api.post(`/admin/users/${user.id}/block`);
                                  toast.success('User blocked successfully');
                                  fetchData();
                                } catch (error) {
                                  toast.error('Failed to block user');
                                }
                              }
                            }}
                            className="text-xs bg-red-500/10 text-red-400 px-2 py-1 rounded hover:bg-red-500 hover:text-white transition-colors"
                          >
                            Block User
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pending KYC */}
              <div className="bg-slate-800 rounded-xl p-6">
                <h2 className="text-xl font-bold text-white mb-4">Pending KYC Approvals</h2>
                {pendingKYC.length > 0 ? (
                  <div className="space-y-3">
                    {pendingKYC.map((kyc) => (
                      <div key={kyc.id} className="p-3 bg-slate-700 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <div className="font-semibold text-white">{kyc.user.email}</div>
                            <div className="text-sm text-gray-400">{kyc.documentType}</div>
                          </div>
                          <div className="text-sm text-yellow-400">{kyc.status}</div>
                        </div>
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => handleKYCApproval(kyc.id, 'approved')}
                            className="flex-1 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleKYCApproval(kyc.id, 'rejected')}
                            className="flex-1 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">No pending KYC</div>
                )}
              </div>
            </div>

            {/* Pending Fiat Deposits */}
            <div className="mt-8 bg-slate-800 rounded-xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">Pending Fiat Deposits (INR)</h2>
              {pendingDeposits.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-gray-400 border-b border-gray-700">
                        <th className="pb-3 px-4">User</th>
                        <th className="pb-3 px-4">Amount</th>
                        <th className="pb-3 px-4">UTR / Ref ID</th>
                        <th className="pb-3 px-4">Date</th>
                        <th className="pb-3 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {pendingDeposits.map((deposit) => (
                        <tr key={deposit.id} className="hover:bg-slate-700/50">
                          <td className="py-4 px-4">
                            <div className="font-semibold text-white">{deposit.user.email}</div>
                            <div className="text-xs text-gray-400">{deposit.user.firstName} {deposit.user.lastName}</div>
                          </td>
                          <td className="py-4 px-4 font-mono text-green-400 font-bold">
                            ₹{parseFloat(deposit.amount).toFixed(2)}
                          </td>
                          <td className="py-4 px-4 text-gray-300 font-mono text-sm">
                            {deposit.referenceId}
                          </td>
                          <td className="py-4 px-4 text-gray-400 text-sm">
                            {new Date(deposit.createdAt).toLocaleString()}
                          </td>
                          <td className="py-4 px-4 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleApproveDeposit(deposit.id)}
                                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-bold transition-colors"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleRejectDeposit(deposit.id)}
                                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold transition-colors"
                              >
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">No pending deposits</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
